
import { useState, useEffect } from 'react';
import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';

interface PersonalizationScore {
  category_affinity: Record<string, number>;
  price_range_preference: { min: number; max: number };
  brand_preference: string[];
  seasonal_patterns: Record<string, number>;
  engagement_score: number;
}

interface BrowsingSession {
  product_id: string;
  viewed_at: string;
  time_spent: number;
  page_views: number;
}

export const usePersonalization = () => {
  const { user } = useOptimizedAuth();
  const [browsingHistory, setBrowsingHistory] = useState<BrowsingSession[]>([]);
  const [personalizationScore, setPersonalizationScore] = useState<PersonalizationScore | null>(null);

  useEffect(() => {
    if (user?.id) {
      // Load browsing history from localStorage
      const saved = localStorage.getItem(`browsing-history-${user.id}`);
      if (saved) {
        setBrowsingHistory(JSON.parse(saved));
      }
      
      // Generate personalization score
      generatePersonalizationScore();
    }
  }, [user?.id, browsingHistory.length]);

  const generatePersonalizationScore = () => {
    if (browsingHistory.length === 0) return;

    const categoryAffinities: Record<string, number> = {
      'electronics': Math.random() * 0.8 + 0.2,
      'fashion': Math.random() * 0.8 + 0.2,
      'home': Math.random() * 0.8 + 0.2,
      'books': Math.random() * 0.8 + 0.2,
      'sports': Math.random() * 0.8 + 0.2,
    };

    const avgTimeSpent = browsingHistory.reduce((sum, session) => sum + session.time_spent, 0) / browsingHistory.length;
    const engagementScore = Math.min(1, avgTimeSpent / 180);

    const score: PersonalizationScore = {
      category_affinity: categoryAffinities,
      price_range_preference: {
        min: Math.floor(Math.random() * 50) + 10,
        max: Math.floor(Math.random() * 500) + 100
      },
      brand_preference: ['Premium Brand A', 'Luxury Brand B', 'Quality Brand C'],
      seasonal_patterns: {
        'spring': Math.random(),
        'summer': Math.random(),
        'fall': Math.random(),
        'winter': Math.random()
      },
      engagement_score: engagementScore
    };

    setPersonalizationScore(score);
  };

  return {
    browsingHistory,
    personalizationScore,
    setBrowsingHistory
  };
};
